import '../../archive/style.scss';

import { DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { Modal } from '@components/common/Modal/Modal';
import { PageTitle } from '@components/common/PageTitle/PageTitle';
import { PagingType, UTable } from '@components/common/UTable/UTable';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { useGetDictionariesQuery } from '@/api/dictionaries/api';
import { useGetOrganizationsShortQuery } from '@/api/organizations/api';
import {
  useDeleteRegistryRecordMutation,
  useGetArchiveRecordsQuery,
} from '@/api/vaccination/registry/api';
import { IndividualShort } from '@/api/vaccination/registry/types';
import { REGISTRY, VACCIANTION } from '@/constants/routes';
import { columns } from '@/pages/Vaccination/components/registry/archive/columns.config';
import { filterSchema } from '@/pages/Vaccination/components/registry/archive/filters.config';
import { Sort } from '@/shared/api/types';
import { OrganizationLevelType } from '@/shared/organizations/enums';
import { vaccination } from '@/shared/security/permissions/vaccination';
import { AppDispatch, RootState } from '@/store';
import { setVaccinationRegistryArchiveFilters } from '@/store/filtersSlice';
import { setVaccinationRegistryArchiveSort } from '@/store/sortSlice';
import { mapTableIndexWithPage } from '@/utils/utils';

export const VaccinationRegistryArchive = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const filters: Record<string, any> = useSelector(
    (state: RootState) => state.filtersSlice.vaccinationRegistryArchive,
  );
  const setFilters = (filters: Record<string, any>) =>
    dispatch(setVaccinationRegistryArchiveFilters(filters));
  const defaultSort: Sort[] = [
    {
      key: 'birthDate',
      order: 'desc',
    },
  ];
  const sort: Sort[] | undefined = useSelector(
    (state: RootState) => state.sorterSlice.vaccinationRegistryArchive,
  );
  const setSort = (sorter: Sort[] | undefined) =>
    dispatch(setVaccinationRegistryArchiveSort(sorter));
  const onSortChange = (sort?: Sort[]) => {
    setSort(sort);
  };
  const [paging, setPaging] = useState<PagingType>({ page: 0, size: 10 });

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [deleteModalData, setDeleteModalData] = useState<{ id?: string }>({});
  const { data: organizations } = useGetOrganizationsShortQuery({
    level: OrganizationLevelType.ORGANIZATION,
  });
  const { data: dictionaries } = useGetDictionariesQuery();
  const { data: records, isLoading } = useGetArchiveRecordsQuery({
    filters,
    paging,
    sort,
  });
  const [deleteRecord, { isLoading: isDeleteRecordLoading }] =
    useDeleteRegistryRecordMutation();

  return (
    <div className="vaccination-archive">
      <PageTitle title={'Архив'} />
      <UTable
        size={'small'}
        data={mapTableIndexWithPage(records?.content)}
        columns={columns(dictionaries)}
        currentSort={sort}
        defaultSort={defaultSort}
        onChange={onSortChange}
        loading={isLoading}
        paging={paging}
        total={records?.totalElements}
        onPagingChange={setPaging}
        buttons={[
          {
            text: 'Назад',
            action: () => navigate(-1),
          },
        ]}
        filter={{
          filters,
          schema: filterSchema(dictionaries, organizations),
          onFilter: (filters) => setFilters(filters),
          onReset: () => setFilters({}),
          security: {
            permissions: vaccination.individual.viewListFiltering,
          },
        }}
        actions={() => [
          {
            icon: <EyeOutlined />,
            action: (record: IndividualShort) =>
              navigate(VACCIANTION + REGISTRY + `/${record?.id}`),
            security: {
              permissions: vaccination.individual.viewPassportArchive,
            },
          },
          {
            icon: <DeleteOutlined />,
            action: (record: IndividualShort) => {
              setDeleteModalData({ id: record.id });
              setIsDeleteModalOpen(true);
            },
            security: {
              permissions: vaccination.individual.deletePassport,
            },
          },
        ]}
      />

      <Modal
        open={isDeleteModalOpen}
        mode={'confirm'}
        text={
          'Вы уверены, что хотите удалить выбранные данные? При выборе варианта «Да» данные восстановлению не подлежат'
        }
        onSubmit={async () => {
          if (deleteModalData?.id) {
            await deleteRecord({ id: deleteModalData.id }).unwrap();
            setIsDeleteModalOpen(false);
          }
        }}
        onCancel={() => {
          setIsDeleteModalOpen(false);
          setDeleteModalData({});
        }}
        confirmLoading={isDeleteRecordLoading}
      />
    </div>
  );
};
